import { useEffect, useMemo } from 'react';
import {
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CommonUtility } from '@utility/common';
import {
  carrierOptions,
  multipleShipmentSchema,
  type ManualShipItem,
  type ManualShipPayload,
  type ManualShipProviderOption,
  type MultipleShipmentValues,
} from '../../lib/manual-ship';
import { getItemTitle } from '../../lib/shipping-form';
import {
  CheckboxField,
  ComboBoxField,
  SelectField,
  TextInputField,
  toSelectOptions,
  type LegacyOption,
} from '../fulfillment/fields';
import { ManualShipActions } from './ManualShipActions';

export type MultipleShipmentFormProps = {
  items: ManualShipItem[];
  providerOptions: ManualShipProviderOption[];
  providersLoading?: boolean;
  carriers: LegacyOption[];
  carriersLoading?: boolean;
  defaultProviderId?: string;
  isSaving?: boolean;
  onSubmit: (payload: ManualShipPayload) => void;
  onCancel: () => void;
};

const SHIP_FROM_HINT =
  'To purchase a label you need at least one manual warehouse with inventory.';

type RowProps = {
  control: Control<MultipleShipmentValues>;
  errors: FieldErrors<MultipleShipmentValues>;
  index: number;
  item: ManualShipItem;
  providerOptions: ManualShipProviderOption[];
  providersLoading?: boolean;
  carriers: LegacyOption[];
  carriersLoading?: boolean;
};

function ItemRow({
  control,
  errors,
  index,
  item,
  providerOptions,
  providersLoading,
  carriers,
  carriersLoading,
}: RowProps) {
  const isChecked = useWatch({ control, name: `details.${index}.checked` });
  const providerId = useWatch({
    control,
    name: `details.${index}.fulfillment_provider_id`,
  });

  const carrier = useMemo(
    () =>
      carrierOptions(
        providerOptions.find(option => option.value === providerId)?.item,
        toSelectOptions(carriers),
      ),
    [providerId, providerOptions, carriers],
  );

  const qty = item.content?.qty ?? item.qty ?? item.quantity;
  const price = item.content?.unit_price ?? item.price;
  const rowErrors = errors.details?.[index];

  return (
    <div className="sk:flex sk:flex-col sk:gap-3 sk:border-b sk:border-rule sk:py-3 sk:last:border-b-0">
      <div className="sk:flex sk:items-center sk:gap-3">
        <CheckboxField
          control={control}
          name={`details.${index}.checked`}
          /* A count, so `=== false` would never hold and never disable a row. */
          isDisabled={!item.available}
          aria-label={getItemTitle(item)}
        />
        {item.p_images?.display_image ? (
          <img
            src={item.p_images.display_image}
            alt=""
            className="sk:size-8 sk:shrink-0 sk:rounded-option sk:object-cover sk:inset-shadow-hairline"
          />
        ) : (
          <span className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-tertiary" />
        )}
        <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-0.5">
          <span
            className="sk:truncate sk:text-dense sk:font-medium"
            title={getItemTitle(item)}
          >
            {getItemTitle(item)}
          </span>
          <span className="sk:flex sk:flex-wrap sk:items-center sk:gap-2 sk:text-xs sk:text-fg-tertiary">
            {item.p_sku ? <span>SKU {item.p_sku}</span> : null}
            {qty ? <span>×{qty}</span> : null}
            {price ? <span>{CommonUtility.currencyFormat(price)}</span> : null}
          </span>
        </div>
      </div>

      {isChecked ? (
        <div className="sk:grid sk:gap-3 sk:pl-9 sk:grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
          <SelectField
            control={control}
            name={`details.${index}.fulfillment_provider_id`}
            label="Ship from"
            hint={SHIP_FROM_HINT}
            placeholder={providersLoading ? 'Loading warehouses' : 'Ship from'}
            options={providerOptions.map(option => ({
              value: option.value,
              label: option.text,
            }))}
            error={rowErrors?.fulfillment_provider_id?.message}
          />
          <ComboBoxField
            control={control}
            name={`details.${index}.provider`}
            label={carrier.label}
            placeholder={`Select or type a ${carrier.label.toLowerCase()}`}
            options={carrier.options}
            isDisabled={carriersLoading}
            error={rowErrors?.provider?.message}
          />
          <TextInputField
            control={control}
            name={`details.${index}.tracking_number`}
            label="Tracking number"
            placeholder="Enter tracking number"
            error={rowErrors?.tracking_number?.message}
          />
        </div>
      ) : null}
    </div>
  );
}

/** A tracking number per item, each shipping from a warehouse of its own. */
export function MultipleShipmentForm({
  items,
  providerOptions,
  providersLoading,
  carriers,
  carriersLoading,
  defaultProviderId = '',
  isSaving,
  onSubmit,
  onCancel,
}: MultipleShipmentFormProps) {
  const itemRowsKey = items
    .map(item => `${item._id}:${item.available === false ? '0' : '1'}`)
    .join('|');
  const defaultValues = useMemo<MultipleShipmentValues>(
    () => ({
      details: itemRowsKey
        ? itemRowsKey.split('|').map(itemRow => ({
            fulfillment_provider_id: defaultProviderId,
            provider: '',
            tracking_number: '',
            checked: itemRow.endsWith(':1'),
          }))
        : [],
    }),
    [itemRowsKey, defaultProviderId],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<MultipleShipmentValues>({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(multipleShipmentSchema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { fields } = useFieldArray({ control, name: 'details' });

  const submit = (values: MultipleShipmentValues) =>
    onSubmit({
      fulfillment_providers: values.details
        .map((detail, index) => ({ detail, item: items[index] }))
        .filter(({ detail, item }) => detail.checked && !!item)
        .map(({ detail, item }) => ({
          fulfillment_provider_id: detail.fulfillment_provider_id,
          items: [{ item_id: item._id }],
          package: '',
          provider: detail.provider,
          service: '',
          tracking_id: detail.tracking_number,
        })),
    });

  return (
    <form
      noValidate
      onSubmit={handleSubmit(submit)}
      className="sk:flex sk:flex-col sk:gap-4"
    >
      <div className="sk:flex sk:flex-col">
        {fields.map((field, index) => (
          <ItemRow
            key={field.id}
            control={control}
            errors={errors}
            index={index}
            item={items[index]}
            providerOptions={providerOptions}
            providersLoading={providersLoading}
            carriers={carriers}
            carriersLoading={carriersLoading}
          />
        ))}
      </div>

      {errors.details?.message ? (
        <span className="sk:text-xs sk:text-danger">
          {errors.details.message}
        </span>
      ) : null}

      <ManualShipActions
        isDisabled={!isValid}
        isSaving={isSaving}
        onCancel={onCancel}
      />
    </form>
  );
}
